import { createRoute, useNavigate } from '@tanstack/react-router';
import { IconStar } from '@tabler/icons-react';
import { MAX_COMPARE_IDS } from '@admitly/shared';
import { Route as appRoute } from '../app.js';
import { useCompare, useShortlist, useToggleShortlist } from '../../hooks/useShortlist.js';
import { CourseCard } from '../../components/courses/CourseCard.js';
import { CompareTable } from '../../components/shortlist/CompareTable.js';
import { Button } from '../../components/ui/Button.js';
import { listIncludes, toggleInList } from '../../components/search/types.js';

type ShortlistSearch = { compare?: string };

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/shortlist',
  validateSearch: (raw: Record<string, unknown>): ShortlistSearch =>
    typeof raw.compare === 'string' && raw.compare.trim() ? { compare: raw.compare } : {},
  component: ShortlistPage,
});

function ShortlistPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data, isLoading, error } = useShortlist();
  const toggle = useToggleShortlist();

  const compareIds = (search.compare ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const setCompare = (next: string | undefined) =>
    void navigate({
      to: '/app/shortlist',
      search: () => (next ? { compare: next } : {}),
    });

  const onToggleCompare = (courseId: string) => {
    // Respect the compare cap; ignore extra selections past the max.
    if (!listIncludes(search.compare, courseId) && compareIds.length >= MAX_COMPARE_IDS) return;
    setCompare(toggleInList(search.compare, courseId));
  };

  const onToggleStar = (courseId: string) => {
    // Everything here is saved; the star unsaves it.
    toggle.mutate({ courseId, saved: true });
  };

  const compareEnabled = compareIds.length >= 2;
  const compare = useCompare(compareEnabled ? compareIds : []);
  const items = data?.items ?? [];

  return (
    <div className="max-w-6xl">
      <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">
        Catalogue
      </div>
      <h1 className="font-serif text-4xl tracking-tighter2 mb-2">My shortlist</h1>
      <p className="text-muted text-sm mb-6">
        {isLoading
          ? 'Loading…'
          : `${items.length} saved course${items.length === 1 ? '' : 's'}` +
            (items.length >= 2 ? ' · tick courses to compare' : '')}
      </p>

      {error && <div className="surface p-5 text-danger text-sm">Couldn't load your shortlist.</div>}

      {!isLoading && items.length === 0 && (
        <div className="surface-soft p-8 text-center">
          <IconStar size={28} className="text-muted-soft mx-auto mb-2" />
          <h3 className="font-serif text-lg text-ink mb-1">No saved courses yet</h3>
          <p className="text-sm text-muted max-w-md mx-auto">
            Star a course from search or its detail page to add it here, then compare your
            favourites side by side.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => (
              <CourseCard
                key={c.id}
                course={c}
                isStarred
                onToggleStar={onToggleStar}
                compareSelectable
                compareSelected={listIncludes(search.compare, c.id)}
                onToggleCompare={onToggleCompare}
              />
            ))}
          </div>

          {compareIds.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted">
                  {compareEnabled
                    ? `Comparing ${compareIds.length} selected`
                    : 'Select at least 2 courses to compare'}
                </p>
                <Button variant="ghost" size="sm" onClick={() => setCompare(undefined)}>
                  Clear comparison
                </Button>
              </div>
              {compareEnabled && compare.data && <CompareTable courses={compare.data.items} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
